/**
 * Pop-up styling system.
 *
 * Three modes (in increasing specificity): theme → preset → custom. Resolved
 * into a single object the renderer applies as inline styles + CSS variables.
 * Every layer is additive: missing fields fall through to the next layer, so
 * a 'custom' popup with only `accent_color` set still inherits everything
 * else from its preset (when chosen) and the tenant theme.
 */

import type { CSSProperties } from 'react'

export type PopupStyleMode = 'theme' | 'preset' | 'custom'

export type PopupStylePreset =
  | 'promo'
  | 'urgent'
  | 'info'
  | 'minimal'
  | 'high_contrast'

export type CustomPopupStyle = {
  background?: string
  text_color?: string
  accent_color?: string
  accent_text_color?: string
  border_color?: string
  border_width_px?: number
  border_radius_px?: number
  shadow?: 'none' | 'soft' | 'strong'
  headline_font?: string
  body_font?: string
  padding_size?: 'sm' | 'md' | 'lg'
  overlay_color?: string
  overlay_opacity?: number
}

/** Final resolved style, ready for inline application. */
export type ResolvedPopupStyle = {
  background: string
  textColor: string
  accentColor: string
  accentTextColor: string
  borderColor: string
  borderWidthPx: number
  borderRadiusPx: number
  shadow: 'none' | 'soft' | 'strong'
  headlineFont: string
  bodyFont: string
  paddingSize: 'sm' | 'md' | 'lg'
  overlayColor: string
  overlayOpacity: number
}

/**
 * Theme-mode fallback values reference the storefront's CSS custom
 * properties (set on <body> in layout.tsx), so the popup picks up the
 * tenant brand for free. var() with a hard fallback so the popup renders
 * sensibly even when consumed outside the storefront (Storybook, preview).
 */
const THEME_DEFAULTS: ResolvedPopupStyle = {
  background: 'var(--color-background, #ffffff)',
  textColor: 'var(--color-text, #111827)',
  accentColor: 'var(--color-cta, #2563eb)',
  accentTextColor: 'var(--color-cta-text, #ffffff)',
  borderColor: 'var(--color-border, #e5e7eb)',
  borderWidthPx: 0,
  borderRadiusPx: 12,
  shadow: 'strong',
  headlineFont: 'var(--brand-font-heading, var(--font-heading, inherit))',
  bodyFont: 'var(--brand-font-body, var(--font-body, inherit))',
  paddingSize: 'md',
  overlayColor: '#000000',
  overlayOpacity: 0.5,
}

export const PRESETS: Record<PopupStylePreset, Partial<ResolvedPopupStyle>> = {
  promo: {
    background: 'var(--color-cta, #2563eb)',
    textColor: 'var(--color-cta-text, #ffffff)',
    accentColor: '#ffffff',
    accentTextColor: 'var(--color-cta, #2563eb)',
    borderRadiusPx: 16,
    shadow: 'strong',
  },
  urgent: {
    background: '#dc2626',
    textColor: '#ffffff',
    accentColor: '#ffffff',
    accentTextColor: '#dc2626',
    borderRadiusPx: 12,
    shadow: 'strong',
  },
  info: {
    background: '#1f2937',
    textColor: '#ffffff',
    accentColor: 'var(--color-cta, #60a5fa)',
    accentTextColor: '#ffffff',
    borderRadiusPx: 12,
    shadow: 'soft',
  },
  minimal: {
    background: '#ffffff',
    textColor: '#111827',
    accentColor: 'var(--color-cta, #2563eb)',
    accentTextColor: '#ffffff',
    borderColor: '#e5e7eb',
    borderWidthPx: 1,
    borderRadiusPx: 8,
    shadow: 'none',
  },
  high_contrast: {
    background: '#000000',
    textColor: '#ffffff',
    accentColor: '#ffffff',
    accentTextColor: '#000000',
    borderRadiusPx: 4,
    shadow: 'none',
    overlayColor: '#000000',
    overlayOpacity: 0.7,
  },
}

const SHADOW_CSS: Record<ResolvedPopupStyle['shadow'], string> = {
  none: 'none',
  soft: '0 4px 12px rgba(0, 0, 0, 0.08)',
  strong: '0 20px 50px rgba(0, 0, 0, 0.25)',
}

const PADDING_CSS: Record<ResolvedPopupStyle['paddingSize'], string> = {
  sm: '1rem',
  md: '1.75rem',
  lg: '2.5rem',
}

export type PopupStyleInput = {
  style_mode: PopupStyleMode
  style_preset: PopupStylePreset | null
  custom_styles: CustomPopupStyle | null
}

export function resolvePopupStyle(input: PopupStyleInput): ResolvedPopupStyle {
  let resolved: ResolvedPopupStyle = { ...THEME_DEFAULTS }

  if (input.style_mode !== 'theme' && input.style_preset) {
    const preset = PRESETS[input.style_preset]
    resolved = { ...resolved, ...preset }
  }

  if (input.style_mode === 'custom' && input.custom_styles) {
    const c = input.custom_styles
    if (c.background) resolved.background = c.background
    if (c.text_color) resolved.textColor = c.text_color
    if (c.accent_color) resolved.accentColor = c.accent_color
    if (c.accent_text_color) resolved.accentTextColor = c.accent_text_color
    if (c.border_color) resolved.borderColor = c.border_color
    if (typeof c.border_width_px === 'number') resolved.borderWidthPx = c.border_width_px
    if (typeof c.border_radius_px === 'number') resolved.borderRadiusPx = c.border_radius_px
    if (c.shadow) resolved.shadow = c.shadow
    if (c.headline_font) resolved.headlineFont = c.headline_font
    if (c.body_font) resolved.bodyFont = c.body_font
    if (c.padding_size) resolved.paddingSize = c.padding_size
    if (c.overlay_color) resolved.overlayColor = c.overlay_color
    if (typeof c.overlay_opacity === 'number') resolved.overlayOpacity = c.overlay_opacity
  }

  return resolved
}

/**
 * Build inline-style + CSS-var object for the popup container. Renderers
 * spread this onto the outermost popup element so CTAs and headings can
 * pick up the same tokens via var().
 */
export function popupStyleToCss(s: ResolvedPopupStyle): CSSProperties {
  return {
    background: s.background,
    color: s.textColor,
    borderColor: s.borderColor,
    borderStyle: s.borderWidthPx > 0 ? 'solid' : 'none',
    borderWidth: `${s.borderWidthPx}px`,
    borderRadius: `${s.borderRadiusPx}px`,
    boxShadow: SHADOW_CSS[s.shadow],
    padding: PADDING_CSS[s.paddingSize],
    fontFamily: s.bodyFont,
    ['--popup-accent' as string]: s.accentColor,
    ['--popup-accent-text' as string]: s.accentTextColor,
    ['--popup-headline-font' as string]: s.headlineFont,
    ['--popup-body-font' as string]: s.bodyFont,
  }
}

export function overlayStyleToCss(s: ResolvedPopupStyle): CSSProperties {
  const opacity = Math.max(0, Math.min(1, s.overlayOpacity))
  return {
    backgroundColor: s.overlayColor,
    opacity,
  }
}
